import React, { useRef } from "react";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import { useSelector } from "react-redux";
import classNames from "classnames";

import Backdrop from "../Backdrop/Backdrop";
import CloseButton from "../Button/CloseButton/CloseButton";
import Typography from "../Typography/Typography";
import ModalActions from "./ModalActions";
import ModalHead from "@/shared/components/modal/ModalHead";

import { useModalContext } from "../../contexts/modal";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import useFocus from "../../hooks/useFocus";
import { useRemToPx } from "@/shared/hooks/viewMode";
import { selectorSetting } from "../../store/settings/selectors";
import { SETTING } from "../../utils/settings/constants";
import {
  handleKeyDownExitFloatWindow,
  isTryingExitElementByButtonTab
} from "../../utils/keyboard-navigation/keyboard-navigation";
import { modalMessageClassName } from "./Modal.constants";

import styles from "./modal.module.scss";

const Modal = ({ props, onClose }) => {
  const {
    dialogId,

    component,
    title,
    message,
    positiveBtn,
    negativeBtn,
    noCancelable,
    className,
    onConfirm,
    onCancel,
    isYesNo,

    confirm,
    translateMessage,

    fixBrokenLayoutAfterOpenFullscreenYoutubeVideoOnIpad,
  } = props;

  const { t } = useSharedTranslation();

  const refRoot = useRef(null);

  const remToPx = useRemToPx();

  const zoom = useSelector(selectorSetting(SETTING.zoom.id));

  useLockBodyScroll(true);
  useFocus(true, refRoot);

  const handleKeyDownRoot = (e) => {
    handleKeyDownExitFloatWindow(e, () => onClose(dialogId));

    if (isTryingExitElementByButtonTab(refRoot.current, e)) {
      e.preventDefault();
    }
  };

  const styleContent = {}, propsMessage = {};

  if (confirm) {
    if (zoom < 300) {
      styleContent.width = remToPx(25.5);
    }
  }

  const messageFinal = message
    ? translateMessage
      ? typeof message === "function"
        ? message({ t })
        : t(message)
      : message
    : "";

  if (typeof messageFinal === "string") {
    propsMessage.dangerouslySetInnerHTML = { __html: messageFinal };
  } else {
    propsMessage.children = messageFinal;
  }

  return (
    <div
      ref={refRoot}
      className={styles["modal"]}
      onClick={(e) => {
        // prevent close float windows (popup, modal, side panel) below
        e.nativeEvent.stopImmediatePropagation();
      }}
      onKeyDown={handleKeyDownRoot}
    >
      <div className={styles["modal-root"]}>
        <div className={classNames(styles["modal-content"], className, {
          [styles["no-animation"]]: fixBrokenLayoutAfterOpenFullscreenYoutubeVideoOnIpad,
        })} style={styleContent}>

          <div className={styles["modal-close-btn"]}>
            <CloseButton onClick={() => onClose(dialogId)} />
          </div>

          {
            title && (
              <ModalHead>
                <Typography className={styles["title"]} variant="h3">{t(title)}</Typography>
              </ModalHead>
            )
          }

          {component ?? (
            <div className={className}>
              <Typography
                variant="body2"
                className={modalMessageClassName}
                {...propsMessage}
              />
            </div>
          )}

          {
            (onConfirm || onCancel) && (
              <ModalActions
                isYesNo={isYesNo}
                positiveText={positiveBtn}
                negativeText={negativeBtn}
                onClickPositive={() => {
                  const confirmReturnValue = onConfirm && onConfirm();
                  if (confirmReturnValue || confirmReturnValue === undefined) {
                    onClose(dialogId);
                  }
                }}
                onClickNegative={() => {
                  if (onCancel) {
                    if (onCancel()) {
                      onClose(dialogId);
                    }
                  } else {
                    onClose(dialogId);
                  }
                }}
              />
            )
          }

          <div className={styles["skirt"]} />
        </div>
      </div>

      <Backdrop
        className={styles["backdrop"]}
        onClick={noCancelable ? undefined : () => onClose(dialogId)}
        clickable={!noCancelable}
      />
    </div>
  );
};

export default Modal;
